import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const SupplyChainOptimization = () => {
  const phrases = [
    "Predictive Logistics",
    "Inventory Orchestration",
    "Value Chain Resilience"
  ];

  const cardData = [
    { type: 'purple-clay', content: '📦' },
    { type: 'smooth-wave', content: '🌐' },
    { type: 'abstract-blocks', content: '🚛' },
    { type: 'pink-sphere', content: '📊' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "📊", title: "Predictive Demand & Forecasting Engines", desc: "Deploy advanced machine learning algorithms that analyze historical velocity, seasonal trends, and market anomalies to prevent overstocks and stockouts." },
    { icon: "🌐", title: "Multi-Echelon Inventory Optimization", desc: "Dynamically calculate safety stock thresholds, reorder parameters, and optimal allocation boundaries across global distribution networks." },
    { icon: "🚛", title: "Dynamic Fleet Routing & Telematics", desc: "Optimize last-mile and line-haul distribution routes in real-time using historical transit telemetry, port congestions, and live fuel cost boundaries." },
    { icon: "🏢", title: "Smart Warehouse Management Systems", desc: "Design highly efficient wave-picking paths, algorithmic slotting layouts, and real-time cross-docking tracking matrices to maximize throughput." },
    { icon: "🔌", title: "End-to-End EDI & API Integration Hubs", desc: "Bridge communication lines natively between your internal ERP architectures and external ocean, rail, and motor carrier tracking mainframes." },
    { icon: "⚖️", title: "Supplier Risk & Sourcing Matrix", desc: "Automate inbound lead-time tracking, lead compliance metrics, and alternate-supplier triggers to prevent tier-1 production halts." },
    { icon: "🛡️", title: "Traceability & Cold Chain Hardening", desc: "Integrate IoT sensory pipelines to actively monitor environment nodes, batch control compliance, and custody transfers globally." }
  ];

  const advancedServices = [
    { icon: "🤖", title: "Autonomous Sourcing Automation", desc: "Program automated micro-tendering routines that instantly book reliable carriers when transactional shipping lane capacity spikes." },
    { icon: "🌍", title: "Carbon Footprint & Scope 3 Audits", desc: "Embed real-time payload emission math models directly inside dispatch modules to track regulatory environmental targets." },
    { icon: "🧮", title: "Total Landed Cost Simulation", desc: "Calculate exact item allocations including custom tariffs, drayage penalties, harbor fees, and mid-mile handling surcharges." },
    { icon: "🔮", title: "Supply Chain Digital Twin Sandboxes", desc: "Build virtualized representations of your distribution footprints to simulate system shocks and network configuration failures safely." },
    { icon: "⚡", title: "Cross-Dock Allocation Engines", desc: "Instantly direct incoming inbound vendor components into active outgoing customer line containers without structural depot storage time." },
    { icon: "📦", title: "Dynamic Package & Pallet Cube Optimization", desc: "Leverage geometric 3D packing algorithms to maximize box density metrics and reduce wasted dead-weight freight overheads." },
    { icon: "🛠️", title: "Predictive Asset Maintenance Links", desc: "Monitor distribution center conveyor arrays and forklift sensors to automatically flag machinery anomalies before failures strike." },
    { icon: "🗄️", title: "Unified Control Tower Interfaces", desc: "Consolidate manufacturing plants, localized depots, transit lines, and customer backlogs into a single pane of glass operational view." },
    { icon: "🧪", title: "Stochastic Network Stress Testing", desc: "Execute automated Monte Carlo runs against supplier performance baselines to reveal hidden structural capacity bottlenecks." }
  ];

  const technologies = {
    "Logistics Core Hubs": ["SAP Extended WMS / EWM", "Oracle Transportation Mgmt", "Blue Yonder Optimization Suite", "Manhattan Active Supply Chain"],
    "Data Intelligence Stack": ["Python (Scikit-Learn / Prophet)", "R Language Statistical Suites", "Apache Spark Stream Processing", "Databricks Lakehouse Pools"],
    "Ecosystem Integration Links": ["MuleSoft Anypoint Gateways", "Apache Kafka Event Triggers", "Core EDI (X12 / EDIFACT) Nodes", "GraphQL Headless Tracking APIs"],
    "IoT & Real-Time Tracking": ["AWS IoT Core / GreenGrass Node", "Project44 Carrier Aggregators", "FourKites Visibility Pipelines", "MQTT / CoAP Sensor Adaptors"],
    "Analytics & Optimization": ["Tableau Operations Cockpits", "Snowflake Analytics Warehouses", "Gurobi Optimization Solvers", "PowerBI Supply Chain Views"]
  };

  const processSteps = [
    { num: "01", title: "Value Chain Discovery & Audit", desc: "Mapping node-to-node operational links, identifying lead-time friction, and auditing legacy inventory metrics." },
    { num: "02", title: "Network Constraint Topology", desc: "Defining clear warehouse capacities, delivery windows, regional transit caps, and compliance guardrails." },
    { num: "03", title: "Algorithmic Engine Tuning", desc: "Configuring prediction systems, building forecasting frameworks, and scripting routing priorities." },
    { num: "04", title: "ERP & Telematics Unification", desc: "Connecting physical IoT channels and legacy transactional data stores seamlessly into unified execution backbones." },
    { num: "05", title: "Edge Case Scenario Straining", desc: "Running heavy network simulation passes, system load tests, and data continuity checks across nodes." },
    { num: "06", title: "Live Node Orchestration", desc: "Migrating routing and tracking operations to active distribution centers with zero disruption to active shipments." },
    { num: "07", title: "Continuous Value Stream Tuning", desc: "Evaluating execution variance, monitoring service compliance, and refining routing thresholds systematically." }
  ];

  const industries = [
    "Global Manufacturing Corporates", "High-Volume Retail & E-Commerce", "Fast-Moving Consumer Goods (FMCG)", "Pharmaceuticals & Cold Chain Life Sciences",
    "Third-Party Logistics (3PL/4PL) Providers", "Automotive Assembly & Just-In-Time Parts", "Aerospace MRO Supply Networks", "High-Tech Electronics & Device Makers",
    "Chemicals, Oil & Gas Distribution", "Food & Beverage Processing Ecosystems", "Construction Material Logistical Hubs", "Wholesale Enterprise Distributors"
  ];

  const designBenefits = [
    "Up to 25% Reduction in Safety Stock", "99.8% On-Time In-Full (OTIF) Delivery", "Minimization of Last-Mile Fuel Surcharges",
    "Total Inbound Freight Tracking Visibility", "Ergonomic Picking Layout Reductions", "Elimination of Manual Allocation Leaks",
    "Resilient Sourcing Pivot Ready Triggers", "Unified Multi-Tier Vendor Governance"
  ];

  const whyChooseUs = [
    "Certified Supply Chain Engineers", "Proven Enterprise Architecture Matrices", "Modern UX Performance Interfaces",
    "Deep ERP & Legacy Integration Depth", "Security-First Encrypted Data Boundaries", "High-Velocity Mathematical Solvers",
    "Comprehensive Post-Launch Maintenance Support", "Transparent Data Migration Models", "Enterprise-Grade SLA Commitments",
    "Experienced Cloud Operations Designers", "Agile Collaborative Dev Cycles", "100% Core Transactional Stability Commitments"
  ];

  return (
    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          Supply Chain Optimization Services | Supply Chain Consulting | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Supply Chain Optimization Services | Supply Chain Consulting | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides supply chain optimization services, including supply chain consulting, logistics optimization, inventory management, demand planning, procurement solutions, warehouse management, transportation optimization, and digital supply chain transformation."
        />

        <meta
          name="keywords"
          content="Supply Chain Optimization, Supply Chain Management, Supply Chain Consulting, Logistics Optimization, Inventory Management, Demand Planning, Procurement Solutions, Warehouse Management, Transportation Management, Supply Chain Analytics, Digital Supply Chain, Supply Chain Automation, Distribution Management, SCM Solutions, Enterprise Supply Chain, Supply Chain Optimization Services, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/supply-chain-optimization"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Supply Chain Optimization Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Optimize your supply chain with Affsoft IT Solutions Pvt. Ltd. We provide logistics optimization, inventory management, procurement, warehouse management, transportation solutions, and digital supply chain transformation."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/supply-chain-optimization"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Supply Chain Optimization Services | Supply Chain Consulting | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Improve operational efficiency with supply chain optimization services from Affsoft IT Solutions Pvt. Ltd. We specialize in logistics, inventory, procurement, warehouse management, and digital supply chain solutions."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="supply-chain-optimization-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#071e22' // Industrial deep teal context fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Supply Chain Optimization &nbsp; and &nbsp;</span>
              <span>Predictive Logistics </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-info fs-3">✳</div>
                <h1 className="fw-bold mb-3 display-5">Supply Chain Optimization</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we eliminate the inefficiencies holding back modern global logistics. Our advanced Supply Chain Optimization architectures link predictive analytics dashboards with real-time tracking systems—reducing working capital demands, optimizing carrier lane capacities, and building hyper-resilient fulfillment cycles.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Audit Your Operations</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor text-info fs-4 animate-blink">|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-info text-dark px-3 py-2 rounded-pill mb-2 text-uppercase">Value Chain Intelligence</span>
              <h2 className="fw-bold text-dark display-6">Synchronize Operations From Sourcing Nodes to Customer Drop Points</h2>
              <p className="text-muted mt-3">
                Fragmented data channels, unmonitored supplier delays, and inaccurate inventory forecasting cause costly operational bottlenecks and emergency freight premiums. At Affsoft IT Solutions Pvt. Ltd., we construct robust, data-driven supply networks engineered for predictability.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3 text-info">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Logistics Performance Benefits Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Quantifiable Capital Gains of Modern Logistics Architectures</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED MODERNIZATION OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-info text-dark px-3 py-2 rounded-pill mb-2 text-uppercase">Algorithmic Logistics</span>
              <h2 className="fw-bold text-dark display-6">High-Yield Operations Systems Engineered for Absolute Traceability</h2>
              <p className="text-muted mt-3">
                We upgrade traditional transactional ERP systems into automated fulfillment platforms. From managing low-latency container message drops to balancing dynamic global hub networks, our codebases safeguard your operational performance.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3 text-info">{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-info text-dark px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold">Ecosystem Frameworks</span>
              <h2 className="fw-bold display-6">Enterprise Infrastructure & Integrations We Support</h2>
              <p className="text-light-50">We orchestrate, patch, and monitor heavy distribution networks using stable enterprise platforms.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold text-info mb-3 border-bottom border-secondary pb-2">{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="text-info opacity-75">•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Lifecycle Architecture</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Supply Chain Roadmap</h2>
              <p className="text-muted">A highly structured implementation path designed to transition your supply systems cleanly from data audit to live automated routing.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-info border-4 h-100 shadow-sm">
                    <div className="fw-bold text-info display-6 mb-2">{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Ecosystems We Optimize</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We pair mathematical network validation with enterprise platform experience to protect your value chain infrastructure.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="text-info fs-4">🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>
    </>
  );
};

export default SupplyChainOptimization;